// externalVideoPlayer.js
// This file implements the functionality for opening videos in a separate window

/**
 * Opens a video in a separate window
 * @param {string} videoPath - Path to the video file
 * @param {string} startTime - Start time in format "HH:MM:SS" or seconds
 * @param {string} endTime - End time in format "HH:MM:SS" or seconds (optional)
 */
export const openExternalVideoPlayer = (videoPath, startTime, endTime) => {
    if (!videoPath) {
      console.error('No video path provided');
      return;
    }
    
    // Process video path
    let videoUrl = videoPath;

    // 确保视频URL是正确的公共路径
    if (!videoPath.startsWith('http')) {
      // 如果是相对路径，确保它以/开头
      videoUrl = videoPath.startsWith('/') ? videoPath : `/${videoPath}`;
    }
    
    
    // Create a minimal HTML for the video player window
    const playerHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Video Player</title>
        <style>
          body {
            margin: 0;
            padding: 0;
            background-color: #000;
            color: #fff;
            font-family: Arial, sans-serif;
            overflow: hidden;
          }
          .video-container {
            width: 100vw;
            height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          video {
            max-width: 100%;
            max-height: 90vh;
            width: auto;
            height: auto;
          }
          .controls {
            position: absolute;
            bottom: 10px;
            left: 0;
            right: 0;
            display: flex;
            justify-content: center;
            gap: 10px;
            padding: 10px;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 10;
          }
          button {
            padding: 8px 16px;
            background-color: #444;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
          }
          button:hover {
            background-color: #555;
          }
          .time-info {
            position: absolute;
            top: 10px;
            left: 10px;
            background-color: rgba(0, 0, 0, 0.6);
            color: white;
            padding: 5px 10px;
            border-radius: 4px;
            font-size: 14px;
          }
        </style>
      </head>
      <body>
        <div class="video-container">
          <video id="player" controls></video>
          <div class="time-info" id="timeInfo"></div>
          <div class="controls">
            <button id="replayBtn">Replay Segment</button>
            <button id="closeBtn">Close Window</button>
          </div>
        </div>
        
        <script>
          // Initialize variables
          const video = document.getElementById('player');
          const timeInfo = document.getElementById('timeInfo');
          const replayBtn = document.getElementById('replayBtn');
          const closeBtn = document.getElementById('closeBtn');
          
          // Parse start and end times
          const startTimeSeconds = ${parseTimeValue(startTime)};
          const endTimeSeconds = ${endTime ? parseTimeValue(endTime) : 'null'};
          
          // Set video source
          video.src = "${videoUrl}";
          
          // When video is loaded, seek to start time
          video.addEventListener('loadedmetadata', function() {
            video.currentTime = startTimeSeconds;
            updateTimeInfo();
          });
          
          // Update time info display
          video.addEventListener('timeupdate', function() {
            updateTimeInfo();
            
            // If end time defined and we've reached it, pause
            if (endTimeSeconds !== null && video.currentTime >= endTimeSeconds) {
              video.pause();
            }
          });
          
          // Replay button
          replayBtn.addEventListener('click', function() {
            video.currentTime = startTimeSeconds;
            video.play();
          });
          
          // Close button
          closeBtn.addEventListener('click', function() {
            window.close();
          });
          
          // Helper function to format time display
          function formatTime(seconds) {
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            const s = Math.floor(seconds % 60);
            
            return h > 0 
              ? \`\${h}:\${m.toString().padStart(2, '0')}:\${s.toString().padStart(2, '0')}\`
              : \`\${m}:\${s.toString().padStart(2, '0')}\`;
          }
          
          // Update time info display
          function updateTimeInfo() {
            timeInfo.textContent = \`Current: \${formatTime(video.currentTime)} | Segment: \${formatTime(startTimeSeconds)} - \${endTimeSeconds ? formatTime(endTimeSeconds) : 'End'}\`;
          }
        </script>
      </body>
      </html>
    `;
    
    // Create a Blob from the HTML string
    const blob = new Blob([playerHTML], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    
    // Open a new window with the player
    const playerWindow = window.open(blobUrl, '_blank', 'width=800,height=600');
    
    // Clean up the Blob URL when window is closed
    if (playerWindow) {
      playerWindow.addEventListener('beforeunload', () => {
        URL.revokeObjectURL(blobUrl);
      });
    }
  };
  
  /**
   * Parses a time value to seconds
   * @param {string|number} timeValue - Time in format "HH:MM:SS", "MM:SS", or seconds
   * @returns {number} Time in seconds
   */
  function parseTimeValue(timeValue) {
    if (!timeValue) return 0;
    
    // If it's already a number or numeric string, return as number
    if (!isNaN(timeValue)) {
      return Number(timeValue);
    }
    
    // If it's a time string, parse it
    const parts = timeValue.split(':').map(p => parseInt(p, 10));
    
    if (parts.length === 3) {
      // HH:MM:SS format
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      // MM:SS format
      return parts[0] * 60 + parts[1];
    } else {
      // Unknown format, return 0
      console.warn(`Unknown time format: ${timeValue}`);
      return 0;
    }
  }